import { useState, useEffect, useMemo, useCallback, KeyboardEvent } from "react";
import { Collapse } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as ChevronRightIcon } from "../../../assets/chevron-right.icon.svg";
import { ReactComponent as ChevronDownIcon } from "../../../assets/chevron-down.icon.svg";
import { RecognitionChunk } from "../../../common/interfaces/admin/recognition-chunk.interface";
import { TableLearningObject } from "../../../common/interfaces/admin/learning-object.interface";
import { MenuItem } from "../../../common/interfaces/comps/menu-item.interface";
import { formatStringDate } from "../../../common/utilities/dates/date-formater.util";
import { fetchRecognitionChunks } from "../../../api/admin/knowledge-base/endpoints-referrers";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { SelectionFilterT } from "../../menu-filter/menu-filter.comp";
import { Loader } from "../../loader/loader.comp";
import MetadataTree from "../metadata-tree/metadata-tree.comp";

const TEXT_PREVIEW_LENGTH = 200;

// Enter/Space activation for the disclosure toggles below, which render as
// <span>/<div> with role="button" rather than native buttons (WCAG 2.1.1).
const clickOnKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
    }
};

type ChunkTextField = "chunk_text" | "chunk_text_plain" | "embedding_text";

const CHUNK_TEXT_OPTIONS: { value: ChunkTextField; label: string }[] = [
    { value: "chunk_text", label: "HTML" },
    { value: "chunk_text_plain", label: "Plain" },
    { value: "embedding_text", label: "Embedding" }
];

const getChunkDisplayText = (chunk: RecognitionChunk, field: ChunkTextField): string => {
    switch (field) {
        case "chunk_text_plain":
            return chunk.chunkTextPlain ?? "(not available)";
        case "embedding_text":
            return chunk.embeddingText ?? "(not available)";
        default:
            return chunk.chunkText;
    }
};

const getPageLabel = (metadata: Record<string, any>): string | null => {
    const pageStart = metadata?.page_start;
    const pageEnd = metadata?.page_end;
    if (pageStart == null) return null;
    if (pageEnd != null && pageEnd !== pageStart) {
        return `Pages ${pageStart}-${pageEnd}`;
    }
    return `Page ${pageStart}`;
};

const ChunkCard = ({ chunk }: { chunk: RecognitionChunk }) => {
    const [textExpanded, setTextExpanded] = useState(false);
    const [metadataExpanded, setMetadataExpanded] = useState(false);
    const [textField, setTextField] = useState<ChunkTextField>("chunk_text");

    const displayText = getChunkDisplayText(chunk, textField);
    const isTextLong = displayText.length > TEXT_PREVIEW_LENGTH;
    const previewText = isTextLong
        ? displayText.substring(0, TEXT_PREVIEW_LENGTH) + "..."
        : displayText;

    const pageLabel = getPageLabel(chunk.metadata);

    const hasMetadata =
        chunk.metadata &&
        typeof chunk.metadata === "object" &&
        Object.keys(chunk.metadata).length > 0;

    return (
        <div className="chunk-card">
            <div className="chunk-card-header">
                <div className="chunk-card-header__left">
                    <span className="chunk-card-header__index">#{chunk.chunkIndex}</span>
                    <span className="chunk-card-header__date">
                        {formatStringDate(chunk.createdAt, "MM.dd.yyyy — hh:mm tt")}
                    </span>
                    {pageLabel && <span className="chunk-card-header__pages">{pageLabel}</span>}
                </div>
                <div className="chunk-card-header__right">
                    <select
                        className="chunk-card-header__text-select"
                        aria-label="Chunk text format"
                        value={textField}
                        onChange={e => {
                            setTextField(e.target.value as ChunkTextField);
                            setTextExpanded(false);
                        }}
                    >
                        {CHUNK_TEXT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="chunk-card-text">
                {isTextLong ? (
                    <>
                        <Collapse in={textExpanded} collapsedSize={60}>
                            <p className="chunk-card-text__content">
                                {textExpanded ? displayText : previewText}
                            </p>
                        </Collapse>
                        <span
                            className="chunk-card-text__toggle"
                            role="button"
                            tabIndex={0}
                            aria-expanded={textExpanded}
                            onClick={() => setTextExpanded(!textExpanded)}
                            onKeyDown={clickOnKey}
                        >
                            {textExpanded ? "Show less" : "Show more"}
                        </span>
                    </>
                ) : (
                    <p className="chunk-card-text__content">{displayText}</p>
                )}
            </div>
            {hasMetadata && (
                <div className="chunk-card-metadata">
                    <div
                        className="chunk-card-metadata__header"
                        role="button"
                        tabIndex={0}
                        aria-expanded={metadataExpanded}
                        onClick={() => setMetadataExpanded(!metadataExpanded)}
                        onKeyDown={clickOnKey}
                    >
                        <SvgIcon
                            className="chunk-card-metadata__chevron"
                            component={metadataExpanded ? ChevronDownIcon : ChevronRightIcon}
                            inheritViewBox
                        />
                        <span className="chunk-card-metadata__label">Metadata</span>
                    </div>
                    <Collapse in={metadataExpanded} unmountOnExit>
                        <div className="chunk-card-metadata__tree">
                            <MetadataTree data={chunk.metadata} />
                        </div>
                    </Collapse>
                </div>
            )}
        </div>
    );
};

export const RecognitionChunksDialog = ({
    open,
    handleClose,
    learningObject,
    companyId,
    knowledgeBaseId
}: {
    open: boolean;
    handleClose: () => void;
    learningObject: TableLearningObject;
    companyId: number;
    knowledgeBaseId: string;
}) => {
    const [chunks, setChunks] = useState<RecognitionChunk[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const rrMenuItems = useMemo<MenuItem<string>[]>(
        () =>
            learningObject.recognitionResults.map(rr => ({
                label: `${rr.name} v.${rr.version}`,
                value: rr.uid
            })),
        [learningObject.recognitionResults]
    );

    const [selectedRR, setSelectedRR] = useState<MenuItem<string>>(
        rrMenuItems.length > 0 ? rrMenuItems[0] : { label: "", value: "" }
    );

    useEffect(() => {
        if (rrMenuItems.length > 0) {
            setSelectedRR(rrMenuItems[0]);
        }
        // Reset the selected result only when the learning object changes;
        // rrMenuItems is derived from it, so keying on loItemId is intentional.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [learningObject.loItemId]);

    const loadChunks = useCallback(
        async (rrUid: string) => {
            if (!rrUid || !knowledgeBaseId) return;
            setIsLoading(true);
            try {
                const response = await fetchRecognitionChunks(companyId, knowledgeBaseId, rrUid);
                setChunks(response.data);
            } catch (error) {
                console.error("Failed to fetch recognition chunks:", error);
                setChunks([]);
            } finally {
                setIsLoading(false);
            }
        },
        [companyId, knowledgeBaseId]
    );

    useEffect(() => {
        if (open && selectedRR.value) {
            loadChunks(selectedRR.value);
        }
    }, [open, selectedRR.value, loadChunks]);

    const handleRRChange = (item: MenuItem<string>) => {
        setSelectedRR(item);
    };

    return (
        <ModalTemplate
            open={open}
            width={900}
            handleClose={() => handleClose()}
            ariaLabel="Recognition Chunks"
        >
            <div className="chunks-dialog">
                <div className="chunks-dialog-header">
                    <h2 className="chunks-dialog-header__title">Recognition Chunks</h2>
                    <span className="chunks-dialog-header__subtitle">{learningObject.name}</span>
                </div>
                {rrMenuItems.length > 1 && (
                    <div className="chunks-dialog-rr-selector">
                        <span className="chunks-dialog-rr-selector__label">
                            Recognition Result:
                        </span>
                        <SelectionFilterT
                            list={rrMenuItems}
                            selectedItem={selectedRR}
                            setSelectedItem={handleRRChange}
                            minWidth={250}
                        />
                    </div>
                )}
                <div className="chunks-dialog-content">
                    {isLoading ? (
                        <div className="chunks-dialog-loader">
                            <Loader size={32} borderWidth={4} />
                        </div>
                    ) : chunks.length === 0 ? (
                        <div className="chunks-dialog-empty">
                            <span>No chunks found for this recognition result</span>
                        </div>
                    ) : (
                        <>
                            <div className="chunks-dialog-count">
                                {chunks.length} chunk{chunks.length !== 1 ? "s" : ""}
                            </div>
                            <div className="chunks-dialog-list">
                                {chunks.map(chunk => (
                                    <ChunkCard key={chunk.id} chunk={chunk} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </ModalTemplate>
    );
};
